import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, onIdleAfterPageLoad } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [productGrid] - The product grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {boolean} */
  #isInitialLoad = true;

  /**
   * Resolves a single ref by name, falling back to a subtree query when the ref
   * is owned by a nested Component. `this.refs` only holds refs whose *closest*
   * Component ancestor is this element; on the collection page the grid is wrapped
   * in `<sticky-content>`, so productGrid/viewMoreNext/pagination/etc. belong to
   * that component and are absent here. Without this fallback renderNextPage and
   * the infinite-scroll observer bail out early and load-more silently no-ops.
   * @param {string} name
   * @returns {any}
   */
  getRef(name) {
    return this.refs[name] ?? this.querySelector(`[ref="${name}"]`);
  }

  /**
   * Hook invoked after a page's items are appended/prepended to the grid.
   * Subclasses override to re-sync per-card attributes that depend on the current
   * client-side layout (e.g. responsive image `sizes` after a column change).
   * Server-rendered pages can carry stale values: the next/previous pages are
   * prefetched and cached, and the column choice persists via a non-awaited cart
   * `fetch`, so neither the cache nor the server is guaranteed to reflect the
   * user's latest columns. No-op by default.
   */
  afterPageRender() {}

  connectedCallback() {
    super.connectedCallback();
    onIdleAfterPageLoad(() => {
      if(!Shopify.designMode) {
        this.#fetchPage('next');
        this.#fetchPage('previous');
      }
      this.#observeViewMore();
    });

    // Listen for filter updates to clear cached pages.
    // Use the private arrow field directly (stable per-instance ref, class-scoped
    // so it never collides with a subclass's own #handleFilterUpdate).
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    setTimeout(() => {
      this.#isInitialLoad = false;
    }, 2000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
  }

  /**
   * Sets up IntersectionObserver to detect when view more buttons enter viewport
   * Triggers infinite scroll by loading next/previous pages automatically
   * Waits for view transitions to complete before loading new content
   */
  #observeViewMore() {
    const viewMorePrevious = this.getRef('viewMorePrevious');
    const viewMoreNext = this.getRef('viewMoreNext');
    const pagination = this.getRef('pagination');

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext && !pagination) return;

    // Skip observer setup for traditional pagination (pages type)
    if (pagination && pagination.getAttribute('data-type') === 'pages') return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const viewMorePrevious = this.getRef('viewMorePrevious');
              const viewMoreNext = this.getRef('viewMoreNext');
              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                // Skip auto-load for load_more type (requires button click)
                if (pagination && pagination.getAttribute('data-type') === 'load_more') return;
                this.renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }


    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * Validates if a page number is within valid range
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const productGrid = this.getRef('productGrid');
    const lastPage = productGrid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * Fetches a page (next or previous) and caches it
   * Resolves promise even if page can't be fetched to unblock waiting renders
   * @param {"previous" | "next"} type - Direction to fetch
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * Fetches a specific page by number and caches the HTML
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);

    this.pages.set(pageNumber, pageContent);
  }

  /**
   * Renders the next page by appending items to the grid
   * Fetches page if not cached, updates pagination summary, and prefetches next page
   */
  async renderNextPage() {
    const productGrid = this.getRef('productGrid');
    const viewMoreNext = this.getRef('viewMoreNext');
    const paginationSummary = this.getRef('paginationSummary');

    if (!productGrid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);
    let nextPagePagination = this.#getViewMoreNextPage(nextPage.page);
    let nextPagePaginationSummary = this.#getPaginationSummary(nextPage.page);
    if (!nextPageItemElements || Shopify.designMode) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      nextPagePagination = this.#getViewMoreNextPage(nextPage.page);
      nextPagePaginationSummary = this.#getPaginationSummary(nextPage.page);
      if (!nextPageItemElements) return;
    }

    productGrid.append(...nextPageItemElements);
    this.afterPageRender();
    if (!nextPagePagination) {
      viewMoreNext.classList.add('!hidden');
      viewMoreNext.parentElement.remove();
    }
    if (nextPagePaginationSummary) {
      paginationSummary.innerHTML = nextPagePaginationSummary.innerHTML;
    }
    if (!this.#isInitialLoad) {
      history.pushState('', '', nextPage.url.toString());
    }

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  /**
   * Renders the previous page by prepending items to the grid
   * Maintains scroll position so user sees same content after prepending
   */
  async #renderPreviousPage() {
    const productGrid = this.getRef('productGrid');

    if (!productGrid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store scroll position before prepending to maintain user's view
    const scrollTop = window.scrollY;
    const firstElement = productGrid.firstElementChild;

    // Record position of first element before prepending
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend previous page items to top of grid
    productGrid.prepend(...previousPageItemElements);
    this.afterPageRender();

    if (!this.#isInitialLoad) {
      history.pushState('', '', previousPage.url.toString());
    }

    // Adjust scroll position so user sees same content after prepending
    // Calculate height difference and offset scroll by that amount
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * Calculates page number and URL for next or previous page
   * Based on first/last card's data-page attribute
   * @param {"previous" | "next"} type - Direction
   * @returns {{ page: number, url: URL } | undefined} Page info or undefined
   */
  #getPage(type) {
    const productGrid = this.getRef('productGrid');
    const cards = Array.from(productGrid?.querySelectorAll(':scope > [ref="cards[]"]') ?? []).filter(
      (card) => card.dataset.productId
    );
    const isPrevious = type === 'previous';

    if (!cards.length) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * Extracts card elements from cached page HTML
   * @param {number} page - Page number
   * @returns {NodeListOf<Element> | undefined} Card elements or undefined
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const productGridElement = parsedPage.querySelector('[ref="productGrid"]');
    if (!productGridElement) return;
    return productGridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  /**
   * Extracts view more next button from cached page HTML
   * @param {number} page - Page number
   * @returns {HTMLElement | undefined} View more element or undefined
   */
  #getViewMoreNextPage(page) {
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;
    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const viewMoreNextElement = parsedPage.querySelector('[ref="viewMoreNext"]');
    return viewMoreNextElement;
  }

  /**
   * Extracts pagination summary from cached page HTML
   * @param {number} page - Page number
   * @returns {HTMLElement | undefined} Pagination summary element or undefined
   */
  #getPaginationSummary(page) {
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;
    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const paginationSummaryElement = parsedPage.querySelector('[ref="paginationSummary"]');
    return paginationSummaryElement;
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.getRef('productGrid')?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.getRef('productGrid')?.dataset.lastPage;
      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const productGrid = this.getRef('productGrid');
    if (productGrid) {
      observer.observe(productGrid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }

        this.#observeViewMore();
        this.#fetchPage('next');
      }, 2000);
    }
  };
}